import React from 'react'
import { HandThumbDownIcon, HandThumbUpIcon, UserIcon } from '@heroicons/react/24/solid'
import { clsx } from 'clsx'
import { MESSAGE_FEEDBACK } from '../../graphql/queries/agent'
import { useMutation } from '@apollo/client'
import {
  MessageContent,
  MessageFeedbackMutation,
  MessageFeedbackMutationVariables,
} from 'app/javascript/components/graphql'
import ThreeDots from '../../../../components/common/ui/ThreeDots'

import { Marked } from 'marked'
import { markedHighlight } from 'marked-highlight'
import hljs from 'highlight.js'
// @ts-ignore
import hlsjLookML from 'highlightjs-lookml'
import 'highlight.js/styles/github.css'
import './messageStyle.css'
import { getRelativeTimeString } from '../../../../components/common/utils/dateDisplay'
import { useChatState } from '../../pages/app/OneAgent/AgentProvider'
import AgentImage from '../AgentImage'

hljs.registerLanguage('lookml', hlsjLookML)

const marked = new Marked(
  markedHighlight({
    langPrefix: 'hljs border rounded shadow syntax-highlighter language-',
    highlight(code, lang) {
      const language = hljs.getLanguage(lang) ? lang : 'plaintext'
      return hljs.highlight(code, { language }).value
    },
  })
)

export const TextContent = ({ content }: { content: MessageContent }) => {
  return (
    <div
      className="prose text-sm max-w-none my-5 mx-2"
      dangerouslySetInnerHTML={{ __html: processText(content.value.trim()) }}
    ></div>
  )
}

const ImageContent = ({ content }: { content: MessageContent }) => {
  return (
    <div className="max-w-3xl m-5">
      <img src={content.value} alt="" className="rounded-md shadow" />
    </div>
  )
}

export type MessageType = {
  content: MessageContent[]
  actor: string
  uuid?: string
  createdAt?: Date
}

interface MessageProps {
  agentUuid: string
  message: MessageType
  showVoting?: boolean
  isStreaming?: boolean
}
const processText = (text: string) => {
  if (!text) {
    return text
  }
  const modifiedText = marked.parse(text, {
    gfm: true,
    breaks: true,
  })
  return modifiedText
}

const Message = ({ agentUuid, message, showVoting = true, isStreaming = false }: MessageProps) => {
  const { agent } = useChatState()
  const [isThumbsUp, setIsThumbsUp] = React.useState<boolean>(null)
  const [MessageFeedback] = useMutation<MessageFeedbackMutation, MessageFeedbackMutationVariables>(
    MESSAGE_FEEDBACK
  )

  const handleThumbsUp = () => {
    setIsThumbsUp(true)
    MessageFeedback({
      variables: {
        agentUuid,
        messageUuid: message.uuid,
        feedback: 'thumbs_up',
      },
    })
  }

  const handleThumbsDown = () => {
    setIsThumbsUp(false)
    MessageFeedback({
      variables: {
        agentUuid,
        messageUuid: message.uuid,
        feedback: 'thumbs_down',
      },
    })
  }

  const actor = message.actor.toLowerCase()
  return (
    <li className="relative flex gap-x-4">
      <div className="">
        <div className="rounded-full h-8 w-8 items-center justify-center flex bg-gray-200 mt-3 text-white shadow-sm ">
          {actor === 'system' ? (
            <AgentImage agent={agent} className="h-8 w-8 rounded-full" />
          ) : (
            <UserIcon className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </div>

      <div className="bg-white flex-grow rounded-md p-3 ring-1 ring-inset ring-gray-300/50">
        <div className="flex justify-between gap-x-4">
          <div className="py-0.5 text-xs leading-5 text-gray-500">
            {actor === 'system' ? (
              <div className="flex flex-row space-x-2">
                <span className="font-medium text-gray-900">{agent.firstName}</span>
                {isStreaming ? (
                  <div className="text-gray-300">
                    <ThreeDots />
                  </div>
                ) : (
                  <div className="">said</div>
                )}
              </div>
            ) : (
              <>
                <span className="font-medium text-gray-900">you</span> asked
              </>
            )}
          </div>

          {message.createdAt && (
            <div className="">
              <span className="text-xs text-gray-500">
                {getRelativeTimeString(message.createdAt)}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col space-x-2">
          {message.content.map((content, index) => {
            return (
              <div key={index}>
                {content.type === 'text' ? (
                  <TextContent content={content} />
                ) : content.type === 'image_file' ? (
                  <ImageContent content={content} />
                ) : null}
              </div>
            )
          })}
        </div>
      </div>

      {actor === 'system' && showVoting && (
        <div className="absolute -right-12 top-1 flex flex-row space-x-2">
          <HandThumbUpIcon
            onClick={handleThumbsUp}
            className={clsx(
              isThumbsUp === true
                ? 'text-green-600'
                : ' text-gray-400 hover:text-gray-500 cursor-pointer',
              'h-4 w-4'
            )}
          />
          <HandThumbDownIcon
            onClick={handleThumbsDown}
            className={clsx(
              isThumbsUp === false
                ? 'text-red-600'
                : ' text-gray-400 hover:text-gray-500 cursor-pointer',
              'h-4 w-4'
            )}
          />
        </div>
      )}
    </li>
  )
}

export default Message
